// TileRegistry
// One Tile per manifest entry. Tiles start "skeletal" — only bbox/center.
// Instances arrive when StreamingController hydrates them from a sidecar.
//
// Tile shape (post-schema-v4):
//   {
//     key,                  // manifest tile id ("3/1/7" or "outlier:0/3")
//     isOutlier,            // true for tiles from the outlier octree
//     inTrimmedBounds,      // legacy flag — still set for compat. True if
//                           //   this tile's bbox intersects the
//                           //   IQR-trimmed scene bounds. For outlier
//                           //   tiles this is false by construction.
//     box,                  // Box3 in world space
//     center,               // Vector3
//     instancesByHash,      // null until sidecar loaded
//     fragmentUrls,         // {rawHash: "fragments/..."} from sidecar (post-v3)
//                           //   null for old exports — controller surfaces error
//     state,                // see TileState
//     lod,                  // current numeric LOD level (or null)
//     sidecarUrl,           // relative URL from manifest (server resolves)
//   }
//
// Schema-v4 outlier model: outliers go through their own octree on the
// exporter side, producing one tile per outlier-octree leaf — each with
// a real bbox. The legacy single bucket (id==="outlier", bbox=null,
// infinite Box3 sentinel) is gone, but we still recognize it for
// back-compat with manifests written before v4.
//
// Authoritative signal for "is this a disabled-tree tile": tile.isOutlier.
// Older code may still read inTrimmedBounds; that keeps working but
// could be tripped by a stray inlier tile whose bbox edges cross the
// trimmed bounds (rare). Prefer isOutlier in new code.
//
// The exporter says instance.m is final world-space placement. Per the
// user confirmation, we set matrices directly with no extra root
// rotation — InstancedMeshes go under an identity Group at the scene
// root.

import { Box3, Vector3 } from 'three';
import { registerNestedRows } from './nestedParts';
import { isInstanceOutOfScope } from './scopeFilter';
import { isInstanceHidden } from './sourceFilterRegistry';
import { applyToRow, expandBox, isInstanceHiddenByOverride } from './documentTransformRegistry';

export const TileState = Object.freeze({
  IDLE:             'idle',
  SIDECAR_LOADING:  'sidecar_loading',
  FRAGMENTS_LOADING:'fragments_loading',
  VISIBLE:          'visible',
  UNLOADED:         'unloaded',
});

export default class TileRegistry {
  constructor({ manifest, trimmedBounds = null }) {
    this.trimmedBounds = trimmedBounds;
    this.tiles = (manifest?.tiles ?? []).map(entry => this._makeTile(entry));
  }

  _makeTile(entry) {
    // isOutlier resolution — three signals, in priority order:
    //   1. New manifest flag entry.outlier === true (schema v4+)
    //   2. Legacy id === "outlier" sentinel (single-bucket pre-v4)
    //   3. Legacy bbox === null (also single-bucket pre-v4)
    // Once any signal triggers we mark isOutlier=true. The id ALSO
    // hints at it via the "outlier:" prefix in v4 ids, but we don't
    // rely on that — the explicit flag is cleaner.
    const isOutlier =
      entry.outlier === true ||
      entry.id === 'outlier' ||
      entry.bbox == null;

    let box, center;

    if (entry.bbox != null) {
      // Real bbox — build a normal Box3. Applies to all inlier tiles
      // and to v4 outlier tiles (which have proper bboxes from their
      // octree leaves).
      box = new Box3(
        new Vector3(...entry.bbox.min),
        new Vector3(...entry.bbox.max)
      );
      // Grown to hold wherever a placement override may have moved
      // this tile's geometry (documentTransformRegistry). Identity
      // when the project has no overrides.
      box = expandBox(box);
      center = new Vector3();
      box.getCenter(center);
    } else {
      // Legacy single-bucket outlier — no bbox. Use the infinite-box
      // sentinel so frustum tests always pass and the tile is treated
      // as "everywhere". Only path that produces this is reading a
      // pre-v4 manifest.
      box = new Box3(
        new Vector3(-Infinity, -Infinity, -Infinity),
        new Vector3( Infinity,  Infinity,  Infinity)
      );
      center = new Vector3(0, 0, 0);
    }

    // inTrimmedBounds — kept for back-compat. Outlier tiles always
    // false (by construction in v4: their bboxes are outside the
    // trimmed bounds). Inlier tiles: true if their bbox intersects
    // the trimmed bounds, which is essentially always true since the
    // trimmed bounds defined the inlier-octree root.
    let inTrimmedBounds;
    if (isOutlier) {
      inTrimmedBounds = false;
    } else if (this.trimmedBounds && entry.bbox != null) {
      inTrimmedBounds = this.trimmedBounds.intersectsBox(box);
    } else {
      inTrimmedBounds = true;
    }

    return {
      key: entry.id,
      isOutlier,
      inTrimmedBounds,
      box,
      center,
      sidecarUrl: entry.url,
      // sidecarFormat 2: binary sidecar next to the JSON one (preferred).
      binUrl: entry.bin || entry.binUrl || null,
      instanceCount: entry.instanceCount ?? 0,
      sizeBytes: entry.sizeBytes ?? 0,
      // Per-tile facts (manifest v2 exports): distinct fragments (= GLB
      // fetches), triangles per LOD, fragment bytes. Absent on older
      // exports.
      hashCount: entry.hashCount ?? 0,
      trisPerLod: Array.isArray(entry.trisPerLod) ? entry.trisPerLod : null,
      fragmentBytes: entry.fragmentBytes ?? 0,
      // Pre-merged singleton meshes, per level of detail:
      // { medium, coarse, items, hashes, tris }. Absent on tiles with
      // nothing to merge and on exports before singletonMerge, which is
      // why the fallback is per tile rather than per model.
      merge: entry.merge || null,
      instancesByHash: null,
      fragmentUrls: null,
      state: TileState.IDLE,
      lod: null,
    };
  }

  // Called by StreamingController once a sidecar arrives. Three outputs
  // attached to the tile:
  //   - instancesByHash: hash → [instance, ...] for materialization
  //   - fragmentUrls:    hash → relative URL for fetching
  //   - sidecar.outlier flag: cross-check vs manifest. Mismatch is a
  //     loud warn (shouldn't happen — manifest writer and tile writer
  //     both source from TileFileWriteResult.OutlierTileIds — but if it
  //     ever does we want to know).
  hydrate(tile, sidecar) {
    const grouped = new Map();
    let skipped = 0;
    let outOfScope = 0;
    (sidecar?.instances ?? []).forEach(inst => {
      // Project "Model files" filter: rows from an unticked source
      // document never reach materialization, so their fragments are
      // not fetched either. No-op on exports without document tags.
      if (isInstanceHidden(inst)) { skipped += 1; return; }
      // Placement overrides (the project card's placement dialog): a
      // hidden file's rows never reach materialisation; a moved file's
      // rows carry the override folded into their matrix, so pools,
      // batches, picking and distance all see the moved geometry.
      if (isInstanceHiddenByOverride(inst)) { skipped += 1; return; }
      // OUTSIDE THE SAVED TRACKING SCOPE (owner, 2026-09-25): a
      // sub-category unticked in the right panel and SAVED is treated as
      // if it had never been selected as a source. Its rows never reach
      // materialisation, so they are never built, never drawn and never
      // held - which is the whole of what he asked for. Filtered HERE,
      // where a row is born, so a tile arriving minutes later is
      // filtered by the same scope without anyone re-planning the
      // stream. No-op on every page that has not set a scope.
      if (isInstanceOutOfScope(inst)) { outOfScope += 1; return; }
      const moved = applyToRow(inst, inst.m, !!inst.cm);
      const normalized = {
        uniqueId: inst.u,
        hash: inst.h,
        deltaMatrix: moved || inst.m,
        cm: moved ? true : !!inst.cm,   // binary rows (and moved rows): matrix already column-major
        disabled: !!inst.d,
        // The top-level family this row is a PART of, when it is one.
        // Undefined on every ordinary row, so it costs nothing on an
        // export with no nesting. See ./nestedParts.
        parentUid: inst.p || undefined,
      };
      const arr = grouped.get(normalized.hash);
      if (arr) arr.push(normalized);
      else grouped.set(normalized.hash, [normalized]);
    });
    tile.instancesByHash = grouped;
    // Learn this tile's part -> parent pairs as it lands. What is known
    // grows with what is loaded, which is right: a part in a tile nobody
    // has fetched cannot be clicked either.
    for (const arr of grouped.values()) registerNestedRows(arr);
    tile.fragmentUrls = sidecar?.fragmentUrls ?? null;
    tile.instancesSkippedBySourceFilter = skipped;
    // Counted separately from the source filter: "the file is hidden"
    // and "this sub-category is not in the tracking scope" are different
    // answers and a single number would conflate them.
    tile.instancesOutOfTrackingScope = outOfScope;

    // Sanity-check the sidecar's outlier flag against the manifest.
    // Disagreement means the export is internally inconsistent; warn
    // and trust the manifest (already baked into tile.isOutlier).
    const sidecarOutlier = sidecar?.outlier === true;
    if (sidecarOutlier !== tile.isOutlier) {
      console.warn(
        `[TileRegistry] Outlier flag mismatch for tile ${tile.key}: ` +
        `manifest=${tile.isOutlier}, sidecar=${sidecarOutlier}. ` +
        `Trusting manifest.`
      );
    }
  }

  forEach(fn) { this.tiles.forEach(fn); }
}